import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useProfileQuery } from "@/features/profile/api/selectProfile";
import Section from "@/features/website/components/Section";
import SectionTitle from "@/features/website/components/SectionTitle";
import { useForm as formspree } from "@formspree/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const FormSchema = z.object({
	name: z
		.string()
		.min(1, {
			message: "This field has to be filled.",
		})
		.max(30, {
			message: "Name must not be longer than 30 letters.",
		}),
	email: z
		.string()
		.min(1, { message: "This field has to be filled." })
		.email("This is not a valid email."),
	message: z
		.string()
		.min(10, {
			message: "Message must be at least 10 characters.",
		})
		.max(500, {
			message: "Message must not be longer than 500 characters.",
		}),
});

const scheduleExamples = [
	{
		title: "Injection Monitoring Schedule",
		content: `- Before Injection
- After Injection
- 6 hours after
- Daily for 2 days from day 1
- Weekly for 20 weeks from week 1`,
	},
	{
		title: "Post-Surgery Recovery Schedule",
		content: `- Day of surgery (baseline)
- 24 hours post-surgery
- 48 hours post-surgery
- Daily for first week
- Weekly for 6 weeks`,
	},
	{
		title: "Medication Efficacy Schedule",
		content: `- Pre-medication baseline
- 1 hour after medication
- 4 hours after medication
- Daily for 5 days
- Monthly follow-up for 6 months`,
	},
	{
		title: "Treatment Response Schedule",
		content: `- Initial assessment
- Mid-treatment (day 14)
- End of treatment
- 3-month follow-up
- 6-month follow-up`,
	},
];

const RequestForm = () => {
	const { data: profile } = useProfileQuery();

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			name: "",
			email: "",
		},
	});

	useEffect(() => {
		if (profile) {
			form.setValue("name", profile.name || "");
			form.setValue("email", profile.email || "");
		}
	}, [profile, form]);

	const [state, handleSubmit] = formspree("xnqkeqbe");

	if (state.succeeded) {
		return (
			<Section>
				<SectionTitle
					left={true}
					title="Thank You for Your Schedule Request"
					caption="Request Sent"
					description="Your schedule request has been sent to the Patient Watch Team, we aim to get back to you within 48 hours."
				/>
			</Section>
		);
	}

	function onSubmit(values: z.infer<typeof FormSchema>) {
		handleSubmit(values);
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className="w-full max-w-lg form"
			>
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Name</FormLabel>
							<FormControl>
								<Input placeholder="Your name" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Email</FormLabel>
							<FormControl>
								<Input placeholder="Your email" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="message"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								Schedule Request Details (see examples below)
							</FormLabel>

							<FormControl>
								<Textarea
									placeholder="Please describe the schedule you need and its purpose"
									className="resize-none"
									{...field}
								/>
							</FormControl>

							<FormMessage />
						</FormItem>
					)}
				/>
				<Accordion type="single" collapsible className="w-full">
					<AccordionItem value="examples">
						<AccordionTrigger>Schedule Examples</AccordionTrigger>
						<AccordionContent>
							<div className="space-y-4">
								{scheduleExamples.map((example) => (
									<div
										key={example.title}
										className="pt-2 border-t"
									>
										<h4 className="font-medium">
											{example.title}
										</h4>
										<div className="mt-1 text-sm whitespace-pre-line">
											{example.content}
										</div>
									</div>
								))}
							</div>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
				<Button type="submit">
					{state.submitting ? "Submitting" : "Submit Request"}
				</Button>
			</form>
		</Form>
	);
};

export default RequestForm;
